/**
 * Record Payment Drawer - Form for recording payments against invoices
 */
import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter
} from '../../ui/sheet';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Checkbox } from '../../ui/checkbox';
import { Loader2, CreditCard, AlertCircle, Info } from 'lucide-react';
import { formatCurrency, formatDateInput, PAYMENT_METHODS } from './utils';

export const RecordPaymentDrawer = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  invoices = [],
  project
}) => {
  const [formData, setFormData] = useState({
    invoice_id: '',
    amount: '',
    payment_date: '',
    payment_method: 'check',
    reference_number: '',
    deposit_to_code: '1000', // Default to Cash
    memo: '',
    allow_overpayment: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Filter to only posted invoices with balance due
  const eligibleInvoices = invoices.filter(
    inv => inv.status === 'posted' && (inv.balance_due > 0 || inv.status !== 'paid')
  );

  // Selected invoice
  const selectedInvoice = eligibleInvoices.find(inv => inv.id === formData.invoice_id);
  const balanceDue = selectedInvoice?.balance_due || 0;
  const isOverpayment = parseFloat(formData.amount) > balanceDue && balanceDue > 0;

  // Reset form when drawer opens
  useEffect(() => {
    if (isOpen) {
      const eligible = invoices.filter(
        inv => inv.status === 'posted' && (inv.balance_due > 0 || inv.status !== 'paid')
      );
      setFormData({
        invoice_id: eligible.length === 1 ? eligible[0].id : '',
        amount: '',
        payment_date: formatDateInput(new Date()),
        payment_method: 'check',
        reference_number: '',
        deposit_to_code: '1000',
        memo: '',
        allow_overpayment: false
      });
      setError(null);
    }
  }, [isOpen, invoices]);

  // Auto-fill amount when invoice selected
  useEffect(() => {
    if (selectedInvoice) {
      setFormData(prev => {
        if (!prev.amount) {
          return { ...prev, amount: selectedInvoice.balance_due?.toString() || '' };
        }
        return prev;
      });
    }
  }, [selectedInvoice]);

  const handleSubmit = async () => {
    // Validation
    if (!formData.invoice_id) {
      setError('Please select an invoice');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }
    if (isOverpayment && !formData.allow_overpayment) {
      setError('Payment exceeds balance due. Check "Allow overpayment" to proceed.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onSubmit({
        invoice_id: formData.invoice_id,
        amount: parseFloat(formData.amount),
        payment_date: new Date(formData.payment_date).toISOString(),
        payment_method: formData.payment_method,
        reference_number: formData.reference_number || null,
        deposit_to_code: formData.deposit_to_code,
        memo: formData.memo || null,
        allow_overpayment: formData.allow_overpayment
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="record-payment-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-green-500" />
            Record Payment
          </SheetTitle>
          <SheetDescription>
            Record a payment against a posted invoice
          </SheetDescription>
        </SheetHeader>

        <div className="py-6 space-y-6">
          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* No eligible invoices warning */}
          {eligibleInvoices.length === 0 && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 text-amber-700 rounded-lg text-sm">
              <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">No eligible invoices</p>
                <p className="text-xs mt-1">
                  Invoices must be <strong>posted</strong> before payments can be applied.
                  Create and post an invoice first.
                </p>
              </div>
            </div>
          )}

          {/* Invoice Selection */}
          <div className="space-y-1.5">
            <Label htmlFor="invoice_id">Invoice *</Label>
            <Select 
              value={formData.invoice_id} 
              onValueChange={(v) => setFormData({ ...formData, invoice_id: v, amount: '' })}
              disabled={eligibleInvoices.length === 0}
            >
              <SelectTrigger id="invoice_id">
                <SelectValue placeholder="Select invoice..." />
              </SelectTrigger>
              <SelectContent>
                {eligibleInvoices.map(inv => (
                  <SelectItem key={inv.id} value={inv.id}>
                    {inv.transaction_number || inv.invoice_number} — Balance: {formatCurrency(inv.balance_due)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Selected invoice info */}
          {selectedInvoice && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600 dark:text-slate-400">Invoice Total:</span>
                <span className="font-medium">{formatCurrency(selectedInvoice.total_amount)}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-slate-600 dark:text-slate-400">Amount Paid:</span>
                <span className="font-medium">{formatCurrency(selectedInvoice.amount_paid || 0)}</span>
              </div>
              <div className="flex justify-between mt-1 pt-1 border-t border-blue-200 dark:border-blue-800">
                <span className="font-medium">Balance Due:</span>
                <span className="font-bold text-amber-600">{formatCurrency(balanceDue)}</span>
              </div>
            </div>
          )}

          {/* Amount and Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="amount">Amount *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                className={isOverpayment ? 'border-amber-500' : ''}
              />
              {isOverpayment && (
                <p className="text-xs text-amber-600">
                  Overpayment: ${(parseFloat(formData.amount) - balanceDue).toFixed(2)} will be held as credit
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="payment_date">Payment Date *</Label>
              <Input
                id="payment_date"
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              />
            </div>
          </div>

          {/* Payment Method and Reference */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="payment_method">Payment Method</Label>
              <Select 
                value={formData.payment_method} 
                onValueChange={(v) => setFormData({ ...formData, payment_method: v })}
              >
                <SelectTrigger id="payment_method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reference_number">Reference / Check #</Label>
              <Input
                id="reference_number"
                value={formData.reference_number}
                onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                placeholder="CHK-12345"
              />
            </div>
          </div>

          {/* Deposit Account */}
          <div className="space-y-1.5">
            <Label htmlFor="deposit_to_code">Deposit To</Label>
            <Select 
              value={formData.deposit_to_code} 
              onValueChange={(v) => setFormData({ ...formData, deposit_to_code: v })}
            >
              <SelectTrigger id="deposit_to_code">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1000">Cash</SelectItem>
                <SelectItem value="1200">Undeposited Funds</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Overpayment checkbox */}
          {isOverpayment && (
            <div className="flex items-center space-x-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <Checkbox
                id="allow_overpayment"
                checked={formData.allow_overpayment}
                onCheckedChange={(checked) => setFormData({ ...formData, allow_overpayment: checked })}
              />
              <Label htmlFor="allow_overpayment" className="text-sm cursor-pointer">
                Allow overpayment (excess will be held as customer credit)
              </Label>
            </div>
          )}

          {/* Memo */}
          <div className="space-y-1.5">
            <Label htmlFor="memo">Memo (optional)</Label>
            <Textarea
              id="memo"
              value={formData.memo}
              onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
              placeholder="Payment notes..."
              className="min-h-[60px]"
            />
          </div>
        </div>

        <SheetFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting || !formData.invoice_id || !formData.amount}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Recording...
              </>
            ) : (
              'Record Payment'
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default RecordPaymentDrawer;
